import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Button, Stack, Chip, CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, FormControlLabel, Checkbox, ToggleButtonGroup, ToggleButton, Tooltip,
  TablePagination,
} from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { type Api, type TenantView, type LeadView, type LeadStatus } from './api';

const STATUS_STYLE: Record<LeadStatus, { label: string; bg: string; fg: string }> = {
  NEW: { label: 'New', bg: 'rgba(37,99,235,0.12)', fg: '#1d4ed8' },
  REVIEWED: { label: 'Reviewed', bg: 'rgba(202,138,4,0.14)', fg: '#a16207' },
  SENT: { label: 'Sent', bg: 'rgba(15,118,110,0.14)', fg: '#0f766e' },
  SPAM: { label: 'Spam', bg: 'rgba(220,38,38,0.12)', fg: '#dc2626' },
};

function StatusPill({ status }: { status: LeadStatus }) {
  const s = STATUS_STYLE[status];
  return <Chip size="small" label={s.label} sx={{ height: 22, fontWeight: 700, bgcolor: s.bg, color: s.fg }} />;
}

const FILTERS: Array<{ key: LeadStatus | 'ALL'; label: string }> = [
  { key: 'ALL', label: 'All' },
  { key: 'NEW', label: 'New' },
  { key: 'REVIEWED', label: 'Reviewed' },
  { key: 'SENT', label: 'Sent' },
  { key: 'SPAM', label: 'Spam' },
];

const fmt = (iso: string) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
const displayName = (l: LeadView) => l.name || [l.firstName, l.lastName].filter(Boolean).join(' ') || '—';

export function Leads({ api, tenant }: { api: Api; tenant: TenantView }) {
  const [leads, setLeads] = useState<LeadView[] | null>(null);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [filter, setFilter] = useState<LeadStatus | 'ALL'>('ALL');
  const [open, setOpen] = useState<LeadView | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState<LeadView | null>(null);
  const [page, setPage] = useState(0);
  const ROWS_PER_PAGE = 10;
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);

  async function load() {
    setLeads(null);
    try {
      const [rows, rec] = await Promise.all([
        api.leads(tenant.tenantId, filter === 'ALL' ? undefined : filter),
        recipients.length ? Promise.resolve({ recipients }) : api.leadRecipients(tenant.tenantId),
      ]);
      setLeads(rows);
      setPage(0);
      if (!recipients.length) setRecipients(rec.recipients);
    } catch (e: any) {
      setToast({ msg: e?.message || 'Failed to load leads', sev: 'error' });
      setLeads([]);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenant.tenantId, filter]);

  function openLead(l: LeadView) {
    setOpen(l);
    // Default: pre-check every recipient (moderator unchecks any they don't want).
    setChecked(new Set(recipients));
  }

  async function setStatus(l: LeadView, status: LeadStatus) {
    setBusy(true);
    try {
      await api.setLeadStatus(tenant.tenantId, l.id, status);
      setToast({ msg: `Marked ${STATUS_STYLE[status].label.toLowerCase()}`, sev: 'info' });
      setOpen(null);
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Update failed', sev: 'error' });
    } finally { setBusy(false); }
  }

  async function remove(l: LeadView) {
    setBusy(true);
    try {
      await api.deleteLead(tenant.tenantId, l.id);
      setToast({ msg: 'Lead deleted', sev: 'info' });
      setConfirmDelete(null);
      setOpen(null);
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Delete failed', sev: 'error' });
    } finally { setBusy(false); }
  }

  async function send(l: LeadView) {
    const to = recipients.filter((r) => checked.has(r));
    if (!to.length) { setToast({ msg: 'Select at least one recipient', sev: 'error' }); return; }
    setBusy(true);
    try {
      const res = await api.sendLead(tenant.tenantId, l.id, to);
      setToast({
        msg: res.dryRun
          ? `Dry run (SMTP not configured) — logged ${to.length} recipient(s). Lead marked sent.`
          : `Sent to ${to.length} recipient(s).`,
        sev: 'success',
      });
      setOpen(null);
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Send failed', sev: 'error' });
    } finally { setBusy(false); }
  }

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    (leads ?? []).forEach((l) => { c[l.status] = (c[l.status] ?? 0) + 1; });
    return c;
  }, [leads]);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <InboxOutlinedIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Leads</Typography>
        {leads && <Chip size="small" label={leads.length} sx={{ height: 22, fontWeight: 700, bgcolor: 'rgba(11,122,117,0.10)', color: 'primary.main' }} />}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every contact-form submission across all sites lands here for review. Vet each one, then forward the genuine leads to the sales team — spam never reaches their inbox.
      </Typography>

      <ToggleButtonGroup
        size="small" exclusive value={filter}
        onChange={(_, v) => v && setFilter(v)}
        sx={{ mb: 2 }}
      >
        {FILTERS.map((f) => (
          <ToggleButton key={f.key} value={f.key} sx={{ textTransform: 'none', px: 2 }}>
            {f.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Paper variant="outlined">
        {!leads ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={26} /></Box>
        ) : leads.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>No leads{filter !== 'ALL' ? ` with status “${filter}”` : ' yet'}.</Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Received</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE).map((l) => (
                  <TableRow key={l.id} hover sx={{ cursor: 'pointer' }} onClick={() => openLead(l)}>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 13 }}>{fmt(l.createdAt)}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{displayName(l)}</TableCell>
                    <TableCell>{l.email}</TableCell>
                    <TableCell>{l.company || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{l.sourceSite || '—'}</TableCell>
                    <TableCell><StatusPill status={l.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {leads && leads.length > ROWS_PER_PAGE && (
          <TablePagination
            component="div"
            count={leads.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            labelRowsPerPage=""
          />
        )}
      </Paper>

      {/* Lead detail + moderation */}
      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="sm" fullWidth>
        {open && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <span>{displayName(open)}</span>
                <StatusPill status={open.status} />
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.2}>
                <Field label="First name" value={open.firstName} />
                <Field label="Last name" value={open.lastName} />
                <Field label="Job title" value={open.title} />
                <Field label="Company" value={open.company} />
                <Field label="Email" value={open.email} />
                <Field label="Phone" value={open.phone} />
                <Field label="Source" value={[open.sourceSite, open.sourcePage].filter(Boolean).join(' · ') || null} />
                <Field label="Received" value={fmt(open.createdAt)} />
                {open.message && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Message</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{open.message}</Typography>
                  </Box>
                )}
                {open.status === 'SENT' && open.sentTo.length > 0 && (
                  <Alert severity="success" variant="outlined" sx={{ mt: 1 }}>
                    Forwarded to {open.sentTo.join(', ')}{open.sentAt ? ` on ${fmt(open.sentAt)}` : ''}.
                  </Alert>
                )}
              </Stack>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Forward to sales</Typography>
              <Typography variant="caption" color="text.secondary">Select who should receive this lead.</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', mt: 1 }}>
                {recipients.map((r) => (
                  <FormControlLabel
                    key={r}
                    control={
                      <Checkbox
                        size="small"
                        checked={checked.has(r)}
                        onChange={(e) => {
                          const next = new Set(checked);
                          if (e.target.checked) next.add(r); else next.delete(r);
                          setChecked(next);
                        }}
                      />
                    }
                    label={<Typography variant="body2">{r}</Typography>}
                  />
                ))}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Mark as spam — never forwarded">
                  <span>
                    <Button size="small" color="error" startIcon={<BlockOutlinedIcon />} disabled={busy} onClick={() => setStatus(open, 'SPAM')}>
                      Spam
                    </Button>
                  </span>
                </Tooltip>
                <Button size="small" startIcon={<DoneOutlinedIcon />} disabled={busy} onClick={() => setStatus(open, 'REVIEWED')}>
                  Mark reviewed
                </Button>
                <Tooltip title="Delete this lead permanently">
                  <span>
                    <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} disabled={busy} onClick={() => setConfirmDelete(open)}>
                      Delete
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
              <Button variant="contained" startIcon={<SendOutlinedIcon />} disabled={busy} onClick={() => send(open)}>
                Send lead
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onClose={() => !busy && setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete lead?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This permanently deletes {confirmDelete ? displayName(confirmDelete) : 'this lead'}&rsquo;s submission. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmDelete(null)} disabled={busy}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteOutlineIcon />}
            disabled={busy}
            onClick={() => confirmDelete && remove(confirmDelete)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)} variant="filled">{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

function Field({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 96, fontWeight: 600 }}>{label}</Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
    </Stack>
  );
}
